import React, { useState, useEffect, useCallback } from 'react';
import { Card, Space, Typography, InputNumber, Button, Tag, Alert, Modal, message } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useCan } from '@refinedev/core';
import { adminApi } from '@/services/api';
import type { AutoplayConfig } from '@/types';

const { Title, Text } = Typography;
const { confirm } = Modal;

/** The price is stored in a Postgres int column; the backend rejects anything above it. */
const MAX_COIN_COST = 2147483647;

const apiMessage = (error: unknown, fallback: string): string => {
  const detail = (error as { response?: { data?: { message?: string | string[] } } })?.response
    ?.data?.message;
  return Array.isArray(detail) ? detail.join(', ') : (detail ?? fallback);
};

/**
 * Player autoplay price: coins per autoplayed innings, served to the client in
 * /v1/config as `autoplay.coinCost`. When it is cleared, /v1/config leaves the
 * key out and the client uses its own temporary price.
 */
export const AutoplayTab: React.FC = () => {
  const { data: canEdit } = useCan({ resource: 'config', action: 'edit' });
  const editable = canEdit?.can !== false;

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState<AutoplayConfig | null>(null);
  const [draft, setDraft] = useState<number | null>(null);

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const res = await adminApi.getAutoplayConfig();
      setConfig(res.data);
      setDraft(res.data.coinCost);
    } catch (error) {
      setLoadError(true);
      message.error(apiMessage(error, 'Failed to load the autoplay price'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  // Nothing is saved until the current price has loaded, so a save can't
  // blindly overwrite a price that was never shown.
  const ready = config !== null;
  const current = config?.coinCost ?? null;
  const draftValid =
    draft !== null && Number.isInteger(draft) && draft >= 1 && draft <= MAX_COIN_COST;

  const save = (coinCost: number | null) => {
    confirm({
      title:
        coinCost === null
          ? 'Clear the autoplay price?'
          : `Set the autoplay price to ${coinCost} coins?`,
      icon: <ExclamationCircleOutlined />,
      content:
        coinCost === null
          ? 'The server stops sending a price, and clients fall back to their own temporary price.'
          : 'Clients see the new price on their next config fetch (at launch or when they return to the hub).',
      onOk: async () => {
        setSaving(true);
        try {
          const res = await adminApi.updateAutoplayConfig(coinCost);
          setConfig(res.data);
          setDraft(res.data.coinCost);
          message.success(coinCost === null ? 'Autoplay price cleared' : 'Autoplay price saved');
        } catch (error) {
          message.error(apiMessage(error, 'Failed to save the autoplay price'));
        } finally {
          setSaving(false);
        }
      },
    });
  };

  if (loadError && !ready) {
    return (
      <Card>
        <Alert
          type="error"
          showIcon
          message="Couldn't load the autoplay price"
          description="Nothing can be changed until the current price has loaded."
          action={<Button onClick={fetchConfig}>Retry</Button>}
        />
      </Card>
    );
  }

  return (
    <Card loading={loading}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <Title level={5}>Autoplay price</Title>
          <Text type="secondary">
            Coins a player pays to simulate one innings instead of playing it, whatever the overs.
            Served to the game in its config.
          </Text>
        </div>

        <Space wrap>
          <Text strong>Current:</Text>
          {current === null ? <Tag>Not configured</Tag> : <Tag color="blue">{current} coins</Tag>}
          {config?.updatedAt && (
            <Text type="secondary">
              Last updated: {new Date(config.updatedAt).toLocaleString()}
            </Text>
          )}
        </Space>

        {ready && current === null && (
          <Alert
            type="info"
            showIcon
            message="No price is set, so the game uses its own temporary price."
          />
        )}

        <Space direction="vertical" size="small" style={{ width: '100%', maxWidth: 320 }}>
          <Text strong>Price (coins per autoplayed innings)</Text>
          <InputNumber
            min={1}
            max={MAX_COIN_COST}
            precision={0}
            value={draft ?? undefined}
            onChange={(v) => setDraft(typeof v === 'number' ? v : null)}
            disabled={!editable || !ready}
            style={{ width: '100%' }}
            placeholder="e.g. 250"
          />
        </Space>

        <Space>
          <Button
            type="primary"
            onClick={() => draftValid && save(draft)}
            loading={saving}
            disabled={!editable || !ready || !draftValid || draft === current}
          >
            Save price
          </Button>
          <Button
            onClick={() => save(null)}
            disabled={!editable || !ready || saving || current === null}
          >
            Clear price
          </Button>
        </Space>

        <Text type="secondary">
          To switch autoplay off in the game, use the <Text code>feature.autoplay</Text> flag on the
          Feature Flags tab.
        </Text>
      </Space>
    </Card>
  );
};
